import { Heightfield } from '../heightfield'
import { Noise2D, type Rng } from '../../core/rng'
import { RAD, clamp01, lerp, smoothstep } from '../../core/math'
import { SNOW_TYPE_COUNT, Snow } from '../snow'
import type { Venue } from './venues'
import type { Weather } from './weather'

/**
 * Step 5 of the pipeline (spec §5): the snow layer.
 *
 * "North-facing holds cold powder, south-facing goes crust and corn,
 * cross-loaded lees pile wind slab, sun-baked convexities go punchy. Aspect +
 * elevation + the day's synthetic weather determines the patchwork."
 *
 * This is the cheapest daily variety in the game and it earns that by being a
 * small physical model rather than four noise fields: wind scours what it faces
 * and loads what it shelters, sun bakes what it strikes, cold preserves, and
 * time since the storm decides how much of any of it survives.
 */
export function paintSnowLayer(
  hf: Heightfield,
  rng: Rng,
  venue: Venue,
  weather: Weather,
): void {
  const patch = new Noise2D(rng)
  const grain = new Noise2D(rng)

  // Direction the wind travels. A slope whose aspect points the same way is in
  // the lee and gets loaded; one facing into it gets scoured.
  const windX = Math.sin(weather.windDir)
  const windZ = Math.cos(weather.windDir)
  // Direction toward the sun.
  const sunX = Math.sin(weather.sunAspect)
  const sunZ = Math.cos(weather.sunAspect)

  // How much of the last storm is still soft, and how well the cold held it.
  const freshness =
    clamp01(Math.exp(-weather.daysSinceSnow / 3.2)) * clamp01(weather.newSnowCm / 45)
  const coldness = clamp01((-weather.tempC + 2) / 14)
  const aged = clamp01(weather.daysSinceSnow / 7)

  const { min: minY, max: maxY } = hf.elevationRange()
  const span = Math.max(1, maxY - minY)

  const w = new Float32Array(SNOW_TYPE_COUNT)

  for (let iz = 0; iz < hf.rows; iz++) {
    const z = hf.zAt(iz)
    for (let ix = 0; ix < hf.cols; ix++) {
      const x = hf.xAt(ix)
      const i = hf.index(ix, iz)

      const slope = hf.slopeAngleAt(x, z) * RAD
      const aspect = hf.aspectAt(x, z)
      const ax = Math.sin(aspect)
      const az = Math.cos(aspect)
      const elev = clamp01((hf.height[i] - minY) / span)
      // Positive = convex/scoured lip, negative = concave/collecting.
      const convex = clamp01(-hf.concavity[i] * 8)
      const concave = clamp01(hf.concavity[i] * 8)

      // How steep the ground is matters for everything: nothing soft stays on
      // a 50° face for long.
      const steepness = smoothstep(30, 52, slope)

      // --- Wind ------------------------------------------------------------
      // +1 fully in the lee, -1 straight into the wind.
      const loading = ax * windX + az * windZ
      const exposedToWind = weather.windStrength * lerp(0.55, 1, elev)
      const lee = clamp01(loading) * exposedToWind
      const scoured = clamp01(-loading) * exposedToWind * lerp(0.6, 1.2, convex)

      // --- Sun -------------------------------------------------------------
      const sunHit = clamp01(ax * sunX + az * sunZ) * weather.sun * lerp(0.7, 1.15, convex)
      // Melt-freeze needs the temperature to actually get near zero.
      const meltFreeze = sunHit * clamp01((weather.tempC + 9) / 11) * lerp(0.35, 1, aged)

      // --- Type weights ----------------------------------------------------
      // Powder: fresh, cold, sheltered from both wind and sun, and collecting.
      let powder =
        freshness * lerp(0.45, 1, coldness) *
        (1 - lee * 0.75) * (1 - scoured * 0.9) * (1 - sunHit * 0.7) *
        lerp(0.75, 1.25, concave) * (1 - steepness * 0.45)

      // Wind slab: cross-loaded lees, needing snow available to move.
      let slab =
        lee * lerp(0.35, 1, clamp01(freshness + 0.35)) * lerp(0.8, 1.15, 1 - concave)

      // Crust: sun-baked and refrozen. Wants age and warmth, hates fresh cold.
      let crust = meltFreeze * lerp(0.4, 1.2, aged) * (1 - freshness * 0.6)

      // Ice: scoured hard by wind, or crust on ground too steep to hold depth.
      let ice =
        scoured * lerp(0.25, 1, steepness) * lerp(0.5, 1, aged) +
        meltFreeze * steepness * 0.55 * lerp(0.3, 1, aged)

      // --- Venue bias and patchiness ---------------------------------------
      const bias = venue.snowBias
      powder += bias[Snow.Powder]
      slab += bias[Snow.WindSlab]
      crust += bias[Snow.Crust]
      ice += bias[Snow.Ice]

      // Patchiness so the boundary is a real edge you can ride along rather
      // than a clean gradient — and so it is learnable across three runs.
      const p1 = patch.fbm(x * 0.012, z * 0.010, 3)
      const p2 = grain.fbm(x * 0.031 + 60, z * 0.028 - 40, 2)
      powder += p1 * 0.22 + p2 * 0.08
      slab += -p1 * 0.16 + p2 * 0.12
      crust += p2 * 0.12
      ice += p1 * 0.06

      // A small floor keeps every cell a blend rather than a hard patchwork,
      // and stops normalisation dividing by ~0 where nothing scored.
      w[Snow.Powder] = Math.max(0.02, powder)
      w[Snow.WindSlab] = Math.max(0.02, slab)
      w[Snow.Crust] = Math.max(0.015, crust)
      w[Snow.Ice] = Math.max(0.01, ice)

      hf.setSnow(i, w)
    }
  }
}

/**
 * Rock, and which slopes will sluff (spec §5.6: "Generation tags which slopes
 * are sluff-prone based on angle and snow type").
 *
 * Rock thresholds sit deliberately above the face's working range — rock means
 * no grip, so it has to stay a feature of cliff bands and couloir walls rather
 * than a coating over every steep turn.
 */
export function paintRockAndSluff(hf: Heightfield, rng: Rng): void {
  const patch = new Noise2D(rng)
  const wts = new Float32Array(SNOW_TYPE_COUNT)

  for (let iz = 0; iz < hf.rows; iz++) {
    const z = hf.zAt(iz)
    for (let ix = 0; ix < hf.cols; ix++) {
      const x = hf.xAt(ix)
      const i = hf.index(ix, iz)
      const slope = hf.slopeAngleAt(x, z) * RAD

      // Onset has to clear the *floor*, not just look right on the walls. Set
      // to 48° it read beautifully on a gully wall and then buried a third of
      // The Gullet's racing line in bare rock, because a 47° floor and a 47°
      // wall are the same number. The walls are 60-70° once their own pitch is
      // added to the mountain's, so that is where the separation lives.
      let rock = smoothstep(56, 70, slope) + 0.55 * smoothstep(64, 78, slope)
      // Strata. Rock outcrops in bands that follow the bedding plane, so the
      // noise is stretched across the slope and tight down it — the difference
      // between a mountainside and a gravel texture.
      const strata = patch.fbm(x * 0.010, z * 0.055 - 70, 3)
      rock += strata * 0.30 * smoothstep(50, 62, slope)
      rock += patch.fbm(x * 0.05 + 130, z * 0.05 - 70, 3) * 0.14
      rock *= smoothstep(0.02, 0.1, z / hf.length) // keep the gate clean
      hf.rock[i] = clamp01(rock - 0.08)

      // Sluff: loose snow releases on steep ground, and only snow that is
      // actually loose. Powder sluffs constantly; ice has nothing to give.
      const o = i * SNOW_TYPE_COUNT
      for (let k = 0; k < SNOW_TYPE_COUNT; k++) wts[k] = hf.snowWeights[o + k]
      const looseness =
        wts[Snow.Powder] * 1.0 + wts[Snow.WindSlab] * 0.25 + wts[Snow.Crust] * 0.1
      hf.sluffProne[i] =
        clamp01(smoothstep(31, 42, slope) * looseness * (1 - hf.rock[i]))
    }
  }
}
